/* eslint-disable max-lines */
import { PageType } from '@app-types/PageType'
import { contentLibrary } from '@components/shared/consts'
import { useUserData } from '@contexts/UserDataContext'
import { getNameByPageType } from 'helpers/userData'
import { FiCheck, FiGrid, FiLayers } from 'react-icons/fi'

// Content type icons mapping based on actual content
const ContentTypeIcons: Record<string, string> = {
  kuran: '📖',
  cevsen: '📿',
  'cevsen-i-serif': '📿',
  celcelutiye: '🤲',
  'evrad-i-kudsiye': '📜',
  'sabah-dualari': '🌅',
  'aksam-dualari': '🌇',
  'tahmidiye-duasi': '⭐',
  'ashab-i-bedir': '🌙',
  'sekine-duasi': '✨',
  'ismi-azam-duasi': '☪️',
  'tercumani-ismi-azam-duasi': '🕌',
  'veysel-karani-munacati': '🧎',
  'delailul-nur': '💫',
  'hulasatul-hulasa': '✴️',
  'munacatul-kuran-duasi': '🕋',
}

// Homepage features with their icons
const HomepageFeatures = [
  {
    id: 'showReadingTime',
    name: 'Okuma Süresi',
    icon: '⏱️',
    description: 'Okuma süresini göster',
  },
  {
    id: 'showConnectionsWidget',
    name: 'Hayırhahlarım',
    icon: '👥',
    description: 'Bağlantıları göster',
  },
  {
    id: 'showPrayerTimes',
    name: 'Namaz Vakitleri',
    icon: '🕌',
    description: 'Namaz vakitlerini göster',
  },
]

// Toggle Switch Component (Visual Only)
const ToggleSwitch = ({
  checked,
  variant = 'default',
}: {
  checked: boolean
  variant?: 'default' | 'accent'
}) => {
  const bgColor = checked
    ? variant === 'accent'
      ? 'bg-primary-500 dark:bg-primary-400'
      : 'bg-secondary-500 dark:bg-secondary-400'
    : 'bg-slate-300 dark:bg-slate-600'

  return (
    <div
      aria-hidden="true"
      className={`
        relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full 
        transition-colors duration-200 ease-in-out
        ${bgColor}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg 
          ring-0 transition-transform duration-200 ease-in-out
          ${checked ? 'translate-x-6' : 'translate-x-1'}
        `}
      >
        {checked && <FiCheck className="absolute inset-0 m-auto w-3 h-3 text-primary-600" />}
      </span>
    </div>
  )
}

// Feature Card Component
const FeatureCard = ({
  icon,
  name,
  description,
  isEnabled,
  onToggle,
}: {
  icon: string
  name: string
  description: string
  isEnabled: boolean
  onToggle: () => void
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={isEnabled}
    onClick={onToggle}
    className={`
      w-full text-left
      group relative overflow-hidden rounded-2xl p-4 cursor-pointer
      transition-all duration-300 ease-out
      focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900
      ${
        isEnabled
          ? 'bg-linear-to-br from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-900/20 ring-2 ring-primary-400/50 dark:ring-primary-500/30'
          : 'bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 ring-1 ring-slate-200 dark:ring-slate-700'
      }
    `}
  >
    {/* Decorative gradient blob */}
    <div
      className={`
        absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl transition-opacity duration-300
        ${isEnabled ? 'bg-primary-400/30 opacity-100' : 'bg-slate-400/10 opacity-0 group-hover:opacity-50'}
      `}
    />

    <div className="relative flex items-center gap-4">
      <div
        className={`
          flex h-12 w-12 items-center justify-center rounded-xl text-2xl
          transition-all duration-300
          ${
            isEnabled
              ? 'bg-white dark:bg-slate-800 shadow-md shadow-primary-500/10'
              : 'bg-white dark:bg-slate-700 shadow-sm'
          }
        `}
      >
        {icon}
      </div>

      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-slate-800 dark:text-slate-100 truncate">
          {name}
        </h4>
        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {description}
        </p>
      </div>

      <ToggleSwitch checked={isEnabled} variant="accent" />
    </div>
  </button>
)

// Content Button Card Component
const ContentCard = ({
  type,
  isEnabled,
  onToggle,
}: {
  type: string
  isEnabled: boolean
  onToggle: () => void
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={isEnabled}
    onClick={onToggle}
    className={`
      w-full text-left
      group relative flex items-center gap-3 p-3 rounded-xl cursor-pointer
      transition-all duration-200 ease-out active:scale-[0.98]
      focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900
      ${
        isEnabled
          ? 'bg-secondary-50 dark:bg-secondary-900/20 ring-2 ring-secondary-400/40 dark:ring-secondary-500/30'
          : 'bg-white dark:bg-slate-800/60 ring-1 ring-slate-200 dark:ring-slate-700 hover:ring-slate-300 dark:hover:ring-slate-600'
      }
    `}
  >
    <div
      className={`
        flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-xl
        transition-all duration-200
        ${
          isEnabled
            ? 'bg-secondary-100 dark:bg-secondary-800/40'
            : 'bg-slate-100 dark:bg-slate-700 group-hover:bg-slate-200 dark:group-hover:bg-slate-600'
        }
      `}
    >
      {ContentTypeIcons[type] || '📋'}
    </div>

    <span
      className={`
        flex-1 font-medium text-sm truncate transition-colors duration-200
        ${isEnabled ? 'text-secondary-900 dark:text-secondary-100' : 'text-slate-700 dark:text-slate-200'}
      `}
    >
      {getNameByPageType(type as PageType)}
    </span>

    <div
      className={`
        flex h-6 w-6 shrink-0 items-center justify-center rounded-full
        transition-all duration-200
        ${
          isEnabled
            ? 'bg-secondary-500 dark:bg-secondary-400 text-white'
            : 'bg-slate-200 dark:bg-slate-600'
        }
      `}
    >
      {isEnabled && <FiCheck className="w-3.5 h-3.5" />}
    </div>
  </button>
)

const HomeButtonsConfig = () => {
  const { userData, updateUserData } = useUserData()

  const handleFeatureToggle = (featureId: string) => {
    updateUserData(
      featureId as keyof typeof userData,
      !userData[featureId as keyof typeof userData],
    )
  }

  const handleContentToggle = (type: PageType) => {
    const homePageButtons = { ...userData.homePageButtons }
    homePageButtons[type] = !homePageButtons[type]
    updateUserData('homePageButtons', homePageButtons)
  }

  const enabledCount = contentLibrary.filter(
    (c) => userData.homePageButtons[c.type],
  ).length

  return (
    <div id="anasayfa-butonlari" className="space-y-8 py-2">
      {/* Homepage Features Section */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/40">
            <FiLayers className="w-4 h-4 text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="font-semibold text-lg text-slate-800 dark:text-slate-100">
            Widget Ayarları
          </h3>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {HomepageFeatures.map((feature) => (
            <FeatureCard
              key={feature.id}
              icon={feature.icon}
              name={feature.name}
              description={feature.description}
              isEnabled={userData[feature.id as keyof typeof userData] as boolean}
              onToggle={() => handleFeatureToggle(feature.id)}
            />
          ))}
        </div>
      </section>

      {/* Content Buttons Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-secondary-100 dark:bg-secondary-900/40">
              <FiGrid className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
            </div>
            <h3 className="font-semibold text-lg text-slate-800 dark:text-slate-100">
              İçerik Butonları
            </h3>
          </div>

          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
            {enabledCount} / {contentLibrary.length} aktif
          </span>
        </div>

        <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {contentLibrary.map((content) => (
            <ContentCard
              key={content.type}
              type={content.type}
              isEnabled={userData.homePageButtons[content.type]}
              onToggle={() => handleContentToggle(content.type)}
            />
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={() => {
              const allEnabled = Object.fromEntries(
                contentLibrary.map((content) => [content.type, true]),
              )
              updateUserData('homePageButtons', allEnabled)
            }}
            className="
              inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium
              bg-secondary-500 hover:bg-secondary-600 active:bg-secondary-700
              text-white shadow-sm shadow-secondary-500/20
              transition-colors duration-150
            "
          >
            <FiCheck className="w-4 h-4" />
            Tümünü Seç
          </button>
          <button
            onClick={() => {
              const allDisabled = Object.fromEntries(
                contentLibrary.map((content) => [content.type, false]),
              )
              updateUserData('homePageButtons', allDisabled)
            }}
            className="
              inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium
              bg-slate-100 hover:bg-slate-200 active:bg-slate-300
              dark:bg-slate-800 dark:hover:bg-slate-700 dark:active:bg-slate-600
              text-slate-700 dark:text-slate-200
              transition-colors duration-150
            "
          >
            Tümünü Kaldır
          </button>
        </div>
      </section>
    </div>
  )
}

export default HomeButtonsConfig
